import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AUTHORIZED_EMAILS = [
  "thornton.carlton68@gmail.com",
  "hbaker@agencyofamerica.com",
];

const SEGMENT_COLORS = {
  hcsm:     { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe' },
  bookstore: { bg: '#fef3c7', text: '#b45309', border: '#fde68a' },
  ebs:      { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  hrm:      { bg: '#fef3c7', text: '#b45309', border: '#fde68a' },
  insureco: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  peo:        { bg: '#fdf4ff', text: '#7e22ce', border: '#e9d5ff' },
  influencer: { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
  mindfulness:{ bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  nonprofit:  { bg: '#eff6ff', text: '#1e3a5f', border: '#bfdbfe' },
  rehab:    { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
  chaplain: { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
};

function formatCurrency(n) {
  if (!n) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function formatDate(str) {
  if (!str) return '—';
  const d = new Date(str + 'Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const LeadsDashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads]     = useState([]);
  const [ambassadors, setAmbassadors] = useState([]);
  const [vcRequests, setVcRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('leads');
  const [status, setStatus]   = useState('loading'); // loading | denied | error | ready
  const [selected, setSelected] = useState(null);
  const [selectedAmb, setSelectedAmb] = useState(null);
  const [selectedVc, setSelectedVc] = useState(null);
  const [filter, setFilter]   = useState('all');

  const apiUrl = window.location.origin;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    let userEmail = '';
    try {
      userEmail = storedUser ? JSON.parse(storedUser).email : '';
    } catch {
      userEmail = '';
    }

    if (!userEmail || !AUTHORIZED_EMAILS.map(e => e.toLowerCase()).includes(userEmail.toLowerCase())) {
      setStatus('denied');
      return;
    }

    fetch(`${apiUrl}/api/v1/leads`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => {
        if (r.status === 403) { setStatus('denied'); return null; }
        if (!r.ok) throw new Error('Server error');
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setLeads(data.leads || []);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));

    fetch(`${apiUrl}/api/v1/ambassador-applications`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAmbassadors(data.applications || []); })
      .catch(() => {});

    fetch(`${apiUrl}/api/v1/vc-requests`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setVcRequests(data.requests || []); })
      .catch(() => {});
  }, []);

  const segments = ['all', 'hcsm', 'ebs', 'hrm', 'insureco', 'peo', 'rehab', 'chaplain', 'ltc', 'influencer', 'mindfulness', 'nonprofit', 'bookstore'];
  const filtered = filter === 'all' ? leads : leads.filter(l => l.partnership_type === filter);

  const totalMonthly = filtered
    .filter(l => l.quote_monthly)
    .reduce((sum, l) => sum + l.quote_monthly, 0);

  const T = {
    pageBg: '#f0f4f8',
    navy: '#1e3a5f',
    heading: '#1e293b',
    body: '#475569',
    bodyLight: '#64748b',
    accent: '#4f46e5',
    accentLight: '#eef2ff',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    green: '#059669',
  };

  if (status === 'loading') return (
    <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: T.bodyLight, fontSize: '1rem' }}>Loading leads…</div>
    </div>
  );

  if (status === 'denied') return (
    <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ color: T.navy, fontWeight: '800', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: T.body, lineHeight: 1.7 }}>You are not authorized to view this page.</p>
        <button onClick={() => navigate('/')} style={{ marginTop: '24px', padding: '12px 28px', borderRadius: '8px', border: 'none', background: T.accent, color: '#fff', fontWeight: '700', cursor: 'pointer' }}>
          Go Home
        </button>
      </div>
    </div>
  );

  if (status === 'error') return (
    <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: T.body }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <p>Error loading leads. Check server logs.</p>
      </div>
    </div>
  );

  return (
    <div style={{ background: T.pageBg, minHeight: '100vh', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Header */}
      <div style={{ background: T.navy, padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', fontWeight: '700', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>FHO Platform</div>
          <h1 style={{ color: '#fff', fontWeight: '800', fontSize: '1.4rem', margin: 0 }}>Partnership Leads</h1>
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#fff' }}>{leads.length}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', fontWeight: '600' }}>Total Leads</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#4ade80' }}>{formatCurrency(totalMonthly) || '—'}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', fontWeight: '600' }}>Pipeline / Mo</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#fbbf24' }}>{ambassadors.length}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', fontWeight: '600' }}>Ambassadors</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '800', color: '#60a5fa' }}>{vcRequests.length}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', fontWeight: '600' }}>Investor Reqs</div>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 32px', display: 'flex', gap: '4px' }}>
        {[['leads', '📋 Partnership Leads'], ['ambassadors', '🎯 Ambassador Applications'], ['vc', '💼 Investor Requests']].map(([tab, label]) => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: '13px', fontWeight: '700', color: activeTab === tab ? '#4f46e5' : '#64748b',
            borderBottom: activeTab === tab ? '2px solid #4f46e5' : '2px solid transparent',
            fontFamily: "'Segoe UI', sans-serif",
          }}>{label}{tab === 'ambassadors' && ambassadors.filter(a => a.status === 'pending').length > 0 && (
            <span style={{ marginLeft: '6px', padding: '1px 7px', borderRadius: '10px', background: '#fef3c7', color: '#b45309', fontSize: '10px', fontWeight: '800' }}>
              {ambassadors.filter(a => a.status === 'pending').length}
            </span>
          )}</button>
        ))}
      </div>

      {activeTab === 'leads' && <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Segment filter */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          {segments.map(seg => (
            <button key={seg} onClick={() => setFilter(seg)} style={{
              padding: '6px 14px', borderRadius: '999px', border: '1.5px solid',
              borderColor: filter === seg ? T.accent : T.cardBorder,
              background: filter === seg ? T.accentLight : T.cardBg,
              color: filter === seg ? T.accent : T.bodyLight,
              fontSize: '12px', fontWeight: '700', cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.05em'
            }}>
              {seg === 'all' ? `All (${leads.length})` : seg.toUpperCase()}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: T.bodyLight }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <p>No leads yet. Share your partnership pages to start collecting quotes.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filtered.map(lead => {
              const seg = lead.partnership_type || 'hcsm';
              const colors = SEGMENT_COLORS[seg] || SEGMENT_COLORS.hcsm;
              const isOpen = selected === lead.id;
              return (
                <div key={lead.id} onClick={() => setSelected(isOpen ? null : lead.id)} style={{
                  background: T.cardBg, borderRadius: '12px', border: '1.5px solid',
                  borderColor: isOpen ? T.accent : T.cardBorder,
                  boxShadow: isOpen ? '0 4px 20px rgba(79,70,229,0.1)' : '0 1px 4px rgba(0,0,0,0.04)',
                  cursor: 'pointer', overflow: 'hidden', transition: 'all 0.2s'
                }}>
                  {/* Row */}
                  <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '180px' }}>
                      <div style={{ fontWeight: '700', color: T.heading, fontSize: '0.97rem' }}>{lead.org_name}</div>
                      <div style={{ fontSize: '0.82rem', color: T.bodyLight, marginTop: '2px' }}>{lead.contact_name} · {lead.title}</div>
                    </div>
                    <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '999px', background: colors.bg, border: '1px solid ' + colors.border, color: colors.text, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {seg}
                    </div>
                    <div style={{ textAlign: 'right', minWidth: '120px' }}>
                      {lead.quote_type === 'revenue_share' ? (
                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: T.green }}>Rev Share</div>
                      ) : lead.quote_type === 'enterprise' ? (
                        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: T.accent }}>Enterprise</div>
                      ) : (
                        <>
                          <div style={{ fontSize: '1.1rem', fontWeight: '800', color: T.green }}>{formatCurrency(lead.quote_monthly)}<span style={{ fontSize: '0.75rem', color: T.bodyLight, fontWeight: '500' }}>/mo</span></div>
                          <div style={{ fontSize: '0.78rem', color: T.bodyLight }}>{lead.quote_tier} · {lead.quote_price}</div>
                        </>
                      )}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: T.bodyLight, minWidth: '140px', textAlign: 'right' }}>{formatDate(lead.created_at)}</div>
                  </div>

                  {/* Expanded */}
                  {isOpen && (
                    <div style={{ padding: '0 20px 20px', borderTop: '1px solid ' + T.cardBorder, paddingTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                      {[
                        ['Email', <a href={`mailto:${lead.email}`} style={{ color: T.accent, textDecoration: 'none', wordBreak: 'break-all' }}>{lead.email}</a>],
                        [lead.partnership_type === 'bookstore' ? 'Database Count' : lead.partnership_type === 'influencer' ? 'Audience Count' : lead.partnership_type === 'rehab' ? 'Patient Count' : lead.partnership_type === 'ltc' ? 'Resident Count' : 'Member / Employee Count', parseInt(lead.member_count).toLocaleString()],
                        ['Segment', lead.segment_label],
                        ['Quote Tier', lead.quote_tier || (lead.quote_type === 'revenue_share' ? 'Revenue Share' : 'Enterprise')],
                        ['Est. Monthly', formatCurrency(lead.quote_monthly) || 'Custom'],
                      ].map(([label, value]) => (
                        <div key={label} style={{ padding: '10px 14px', borderRadius: '8px', background: T.pageBg }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', color: T.bodyLight, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{label}</div>
                          <div style={{ fontSize: '0.9rem', fontWeight: '600', color: T.heading, overflowWrap: 'break-word', minWidth: 0 }}>{value}</div>
                        </div>
                      ))}
                      <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <a href={`mailto:${lead.email}?subject=FHO Platform Partnership — Following Up&body=Hi ${lead.contact_name},%0A%0AThank you for your interest in the FHO Platform partnership for ${lead.org_name}.`}
                          style={{ padding: '10px 20px', borderRadius: '8px', background: T.accent, color: '#fff', fontWeight: '700', fontSize: '0.85rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
                          onClick={e => e.stopPropagation()}>
                          Follow Up →
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {activeTab === 'ambassadors' && (
        <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>
          {ambassadors.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
              <p>No ambassador applications yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {ambassadors.map(app => {
                const isOpen = selectedAmb === app.id;
                const sc = { pending: { bg: '#fef3c7', text: '#b45309', border: '#fde68a' }, approved: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' }, rejected: { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' } }[app.status] || { bg: '#fef3c7', text: '#b45309', border: '#fde68a' };
                const fmtF = n => { if (!n) return '—'; if (n >= 1000000) return `${(n/1000000).toFixed(1)}M`; if (n >= 1000) return `${(n/1000).toFixed(1)}K`; return n.toString(); };
                return (
                  <div key={app.id} onClick={() => setSelectedAmb(isOpen ? null : app.id)} style={{ background: '#fff', borderRadius: '12px', border: `1.5px solid ${isOpen ? '#4f46e5' : '#e2e8f0'}`, boxShadow: isOpen ? '0 4px 20px rgba(79,70,229,0.1)' : '0 1px 4px rgba(0,0,0,0.04)', cursor: 'pointer', overflow: 'hidden', transition: 'all 0.2s' }}>
                    <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1', minWidth: '180px' }}>
                        <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.97rem' }}>{app.name}</div>
                        <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}>{app.email}</div>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e3a5f', textTransform: 'capitalize' }}>{(app.platform || '').replace('_', ' ')}</div>
                      <div style={{ fontSize: '13px', fontWeight: '800', color: '#059669' }}>{fmtF(app.follower_count)} followers</div>
                      <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '999px', background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{app.status}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{formatDate(app.created_at)}</div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '16px 20px 20px', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                          {[['Profile URL', <a href={app.profile_url} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', textDecoration: 'none', wordBreak: 'break-all' }} onClick={e => e.stopPropagation()}>{app.profile_url}</a>], ['Content Niche', app.content_niche || '—'], ['Secondary Platform', app.secondary_platform ? `${app.secondary_platform} — ${fmtF(app.secondary_followers)}` : '—'], ['Audience', app.audience_description || '—']].map(([label, value]) => (
                            <div key={label} style={{ padding: '10px 14px', borderRadius: '8px', background: '#f0f4f8' }}>
                              <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{label}</div>
                              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b' }}>{value}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                          <a href={`mailto:${app.email}?subject=Your FHO Ambassador Application — Approved!`} style={{ padding: '10px 20px', borderRadius: '8px', background: '#059669', color: '#fff', fontWeight: '700', fontSize: '0.85rem', textDecoration: 'none' }}>✓ Approve & Email</a>
                          <a href={app.profile_url} target="_blank" rel="noreferrer" style={{ padding: '10px 20px', borderRadius: '8px', background: '#4f46e5', color: '#fff', fontWeight: '700', fontSize: '0.85rem', textDecoration: 'none' }}>View Profile →</a>
                          <a href={`mailto:${app.email}?subject=Your FHO Ambassador Application`} style={{ padding: '10px 20px', borderRadius: '8px', background: '#f0f4f8', color: '#1e3a5f', fontWeight: '700', fontSize: '0.85rem', textDecoration: 'none', border: '1px solid #e2e8f0' }}>Email Applicant</a>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'vc' && (
        <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>
          {vcRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
              <p>No investor requests yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {vcRequests.map(req => {
                const isOpen = selectedVc === req.id;
                const intentColors = req.intent === 'access'
                  ? { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe', label: 'Platform Access' }
                  : { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0', label: 'Conversation' };
                const statusColors = { new: { bg: '#fef3c7', text: '#b45309' }, granted: { bg: '#ecfdf5', text: '#059669' }, declined: { bg: '#fef2f2', text: '#dc2626' } }[req.status] || { bg: '#fef3c7', text: '#b45309' };
                return (
                  <div key={req.id} onClick={() => setSelectedVc(isOpen ? null : req.id)} style={{ background: '#fff', borderRadius: '12px', border: `1.5px solid ${isOpen ? '#4f46e5' : '#e2e8f0'}`, boxShadow: isOpen ? '0 4px 20px rgba(79,70,229,0.1)' : '0 1px 4px rgba(0,0,0,0.04)', cursor: 'pointer', overflow: 'hidden', transition: 'all 0.2s' }}>
                    <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      <div style={{ flex: '1', minWidth: '180px' }}>
                        <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.97rem' }}>{req.firm || '(no firm given)'}</div>
                        <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '2px' }}>{req.contact_name || '—'}{req.role ? ' · ' + req.role : ''}</div>
                      </div>
                      <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '999px', background: intentColors.bg, border: `1px solid ${intentColors.border}`, color: intentColors.text, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{intentColors.label}</div>
                      <div style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '999px', background: statusColors.bg, color: statusColors.text, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{req.status}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', minWidth: '140px', textAlign: 'right' }}>{formatDate(req.created_at)}</div>
                    </div>
                    {isOpen && (
                      <div style={{ padding: '16px 20px 20px', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                          {[['Email', <a href={`mailto:${req.email}`} style={{ color: '#4f46e5', textDecoration: 'none', wordBreak: 'break-all' }} onClick={e => e.stopPropagation()}>{req.email}</a>], ['Intent', intentColors.label], ['Fund stage / focus', req.aum_or_stage || '—'], ['Message', req.message || '—']].map(([label, value]) => (
                            <div key={label} style={{ padding: '10px 14px', borderRadius: '8px', background: '#f0f4f8' }}>
                              <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{label}</div>
                              <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b', overflowWrap: 'break-word', minWidth: 0 }}>{value}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                          <a href={`mailto:${req.email}?subject=FHO Platform — ${intentColors.label}&body=Hi ${req.contact_name || 'there'},%0A%0AThank you for your interest in evaluating the FHO Platform.`} style={{ padding: '10px 20px', borderRadius: '8px', background: '#4f46e5', color: '#fff', fontWeight: '700', fontSize: '0.85rem', textDecoration: 'none' }}>Follow Up →</a>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        @media (max-width: 640px) {
          div[style*="padding: '28px 32px'"] { padding: 16px !important; }
        }
      `}</style>
    </div>
  );
};

export default LeadsDashboard;
